import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { SimpleBars } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { downloadCsv, filtersToQuery, hourLabel, km, mins, num, pct, type TimingReport } from '../lib/reporting';

const PRIMARY = CHANNEL_COLORS.NVS;

export function Timing() {
  // Reuses the shared NVS filter bar (date / region / store / driver) but reads
  // its own timing aggregate from /api/reports/timing.
  const { filters } = useReport();
  const [data, setData] = useState<TimingReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!filters.from || !filters.to) return;
    let active = true;
    setError(null);
    apiFetch<TimingReport>(`/api/reports/timing?${filtersToQuery(filters)}`)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load timing report');
      });
    return () => {
      active = false;
    };
  }, [filters]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(
      `nvs-timing-by-region-${filters.from}_${filters.to}.csv`,
      data.byRegion.map((r) => ({
        region: r.region,
        completed_stops: r.stops,
        avg_mins_on_site: r.avgMinsOnSite ?? '',
        avg_distance_km: r.avgKm ?? '',
      })),
    );
  };

  const t = data?.totals;
  const peakHour =
    data && data.byHour.length
      ? data.byHour.reduce((a, b) => (b.stops > a.stops ? b : a))
      : null;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / Delivery timing"
        title="Delivery timing"
        subtitle={
          data
            ? `NVS in-house · ${data.from} → ${data.to} · completion time-of-day, on-site dwell & transit (times in UK local time). DPD handoffs excluded.`
            : 'NVS in-house · completion time-of-day, dwell & transit'
        }
      />
      <FilterBar onExport={exportCsv} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}

      {data && t && t.completedStops === 0 && (
        <div className="empty-note">
          <strong>No completed stops in this range.</strong>
          <p className="muted">Timing metrics are computed from completed deliveries only.</p>
        </div>
      )}

      {data && t && t.completedStops > 0 && (
        <>
          <div className="kpi-grid">
            <KpiCard
              label="Completed before noon"
              value={pct(t.pctBeforeNoon)}
              sub="Punctuality proxy (no SLA window in source)"
              tone={t.pctBeforeNoon != null && t.pctBeforeNoon >= 0.7 ? 'good' : 'neutral'}
              accent={PRIMARY}
            />
            <KpiCard label="Avg time on site" value={mins(t.avgMinsOnSite)} sub={`Median ${mins(t.medianMinsOnSite)} · arrived → completed`} accent={PRIMARY} />
            <KpiCard label="Avg transit" value={mins(t.avgTransitMins)} sub="Enroute → arrived" accent={PRIMARY} />
            <KpiCard label="Avg distance" value={km(t.avgKm)} sub={`${num(t.completedStops)} completed stops`} accent={PRIMARY} />
          </div>

          <Panel
            title="Completions by time of day"
            subtitle={peakHour ? `Busiest hour: ${hourLabel(peakHour.hour)} (${num(peakHour.stops)} stops) · UK local time` : 'UK local time'}
          >
            {data.byHour.length > 0 ? (
              <SimpleBars
                data={data.byHour.map((h) => ({ label: hourLabel(h.hour), value: h.stops }))}
                format={(v) => num(v)}
                color={PRIMARY}
              />
            ) : (
              <p className="muted">No completion timestamps in this range.</p>
            )}
          </Panel>

          <Panel title="On-site dwell & distance by region" subtitle="Completed stops per region">
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th className="num">Completed</th>
                    <th className="num">Avg on site</th>
                    <th className="num">Avg distance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byRegion.map((r) => (
                    <tr key={r.region}>
                      <td>{r.region}</td>
                      <td className="num">{num(r.stops)}</td>
                      <td className="num">{mins(r.avgMinsOnSite)}</td>
                      <td className="num">{km(r.avgKm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="On-site dwell & distance by driver" subtitle={`${num(data.byDriver.length)} drivers (top 25 by volume)`}>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th className="num">Completed</th>
                    <th className="num">Avg on site</th>
                    <th className="num">Avg distance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byDriver.map((d) => (
                    <tr key={d.driver}>
                      <td>{d.driver}</td>
                      <td className="num">{num(d.stops)}</td>
                      <td className="num">{mins(d.avgMinsOnSite)}</td>
                      <td className="num">{km(d.avgKm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </section>
  );
}
